import React, { useState, useEffect } from 'react';
import { db } from '../services/FishingDB';
import { FishingSession } from '../entities/FishingEntities';
import { useLanguage } from '../contexts/LanguageContext';
import { getActiveSession, endSession } from '../services/FishingService';
import SessionStartModal from '../components/SessionStartModal';
import Toast from '../components/Toast';
import '../App.css';
import '../styles/Pages.css';

export const SessionsPage: React.FC = () => {
  const [sessions, setSessions] = useState<FishingSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [showStartModal, setShowStartModal] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'info' | 'success' | 'error'>('info');
  const [hasActiveSession, setHasActiveSession] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const data = await db.sessions.orderBy('startTime').reverse().toArray();
      setSessions(data);
      
      // Check if there's an active session
      const active = await getActiveSession();
      setHasActiveSession(!!active);
    } catch (error) {
      console.error('Failed to load sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message: string, type: 'info' | 'success' | 'error' = 'info') => {
    setToastMessage(message);
    setToastType(type);
    setTimeout(() => setToastMessage(''), 3000);
  };

  const handleSessionStarted = (message: string, type?: 'info' | 'success' | 'error') => {
    showToast(message, type);
    loadData(); // Reload sessions
  };

  const handleEndSession = async (sessionId: string) => {
    try {
      await endSession(sessionId);
      showToast('✅ Fiskepass avslutat!', 'success');
      loadData(); // Reload sessions
    } catch (error) {
      console.error('Failed to end session:', error);
      showToast('❌ Kunde inte avsluta pass', 'error');
    }
  };

  const formatDuration = (startTime: number, endTime?: number) => {
    const duration = (endTime || Date.now()) - startTime;
    const hours = Math.floor(duration / (1000 * 60 * 60));
    const minutes = Math.floor((duration % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  if (loading) {
    return (
      <div className="text-center py-20 text-white/70">
        <p>{t.loadingSessions}</p>
      </div>
    );
  }

  return (
    <>
      <div className="page-header">
            <div>
              <h1 style={{ color: 'white' }}>📋 {t.sessions}</h1>
              <p className="subtitle" style={{ color: 'rgba(255,255,255,0.7)' }}>
                {sessions.length} {t.sessionsTotal}, {sessions.filter(s => s.isActive).length} {t.active}
              </p>
            </div>
            <button
              onClick={() => setShowStartModal(true)}
              style={{
                padding: '12px 24px',
                background: hasActiveSession 
                  ? 'linear-gradient(135deg, rgb(239, 68, 68) 0%, rgb(220, 38, 38) 100%)'
                  : 'linear-gradient(135deg, rgb(59, 130, 246) 0%, rgb(37, 99, 235) 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: hasActiveSession
                  ? '0 4px 12px rgba(239, 68, 68, 0.3)'
                  : '0 4px 12px rgba(59, 130, 246, 0.3)',
              }}
            >
              {hasActiveSession ? '🔄 Starta Nytt Pass' : '🎣 Starta Pass'}
            </button>
          </div>

        {sessions.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📋</div>
            <h2>{t.noSessionsYet}</h2>
            <p>{t.noSessionsDescription}</p>
          </div>
        ) : (
          <div className="sessions-container">
            {sessions.map(session => (
              <div key={session.id} className={`session-card ${session.isActive ? 'active' : ''}`}>
                <div className="session-header">
                  <div className="session-title">
                    <h3>🎣 {t.fishingSession}</h3>
                    <span className={`session-badge ${session.isActive ? 'active' : 'completed'}`}>
                      {session.isActive ? t.active : t.completed}
                    </span>
                  </div>
                </div>

                <div className="session-stats">
                  <div className="stat-item">
                    <span className="stat-label">{t.start}</span>
                    <span className="stat-value">
                      {new Date(session.startTime).toLocaleString('sv-SE', {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>

                  <div className="stat-item">
                    <span className="stat-label">{t.duration}</span>
                    <span className="stat-value">
                      {formatDuration(session.startTime, session.endTime)}
                    </span>
                  </div>

                  <div className="stat-item">
                    <span className="stat-label">{t.catches}</span>
                    <span className="stat-value">{session.catchCount}</span>
                  </div>

                  {session.waterTemp && (
                    <div className="stat-item">
                      <span className="stat-label">{t.waterTemp}</span>
                      <span className="stat-value">{session.waterTemp}°C</span>
                    </div>
                  )}
                </div>

                {/* Location name removed - sessions don't have location names */}

                {session.notes && (
                  <div className="catch-notes" style={{ marginTop: '8px' }}>
                    {session.notes}
                  </div>
                )}

                {/* End Session Button (only for active sessions) */}
                {session.isActive && (
                  <button
                    onClick={() => handleEndSession(session.id)}
                    style={{
                      marginTop: '16px',
                      width: '100%',
                      padding: '12px',
                      background: 'linear-gradient(135deg, rgb(239, 68, 68) 0%, rgb(220, 38, 38) 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '12px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
                      transition: 'transform 0.2s',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                  >
                    🏁 Avsluta Pass
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      
      {/* Session Start Modal */}
      <SessionStartModal
        isOpen={showStartModal}
        onClose={() => setShowStartModal(false)}
        onSuccess={handleSessionStarted}
      />
      
      {/* Toast notifications */}
      {toastMessage && (
        <Toast 
          message={toastMessage} 
          type={toastType} 
        />
      )}
    </>
  );
};
